import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import ConditionTap from 'lib/Tracking/Srp/SearchFilters/ConditionTap';
import FeaturesTechnologyTap from 'lib/Tracking/Srp/SearchFilters/FeaturesTechnologyTap';
import ExpandCollapseTap from 'lib/Tracking/Srp/SearchFilters/ExpandCollapseTap';
import { useConditionFilter, useFeaturesTechnologyFilter } from 'lib/hooks/useSrpFilters';
import useEvents from 'lib/hooks/useEvents';
import { Features } from 'lib/EventSchema';

import Accordion from 'components/Shared/SearchFilter/Accordion';
import LabeledCheckBox from 'components/Shared/SearchFilter/CheckBox/LabeledCheckBox';
import PageDivider from 'components/Shared/SearchFilter/PageDivider';
import Toggle from 'components/Shared/SearchFilter/Toggle';

import { SrpConditionFilterValue, SrpConditionName } from 'types/srpFilters';
import { useContext } from 'react';
import SrpEventContext from 'contexts/SrpEventContext';
import { ConditionFacet } from 'lib/AutolistAPI';
import { formatNumberWithCommas } from 'lib/utils';
import classNames from 'classnames';
import { colors } from 'components/styles/variables';

export interface ConditionFilterProps {
  expanded?: boolean;
  conditionFacets?: ConditionFacet;
}

export const conditionFilterDefaultState: SrpConditionFilterValue = {
  new: false,
  used: false,
  'certified pre-owned': false,
};

export enum Conditions {
  new = 'new',
  used = 'used',
  cpo = 'cpo',
  inclWarranty = 'warranty',
}

interface ConditionOptionProps {
  type: SrpConditionName;
  label: string;
  isChecked: boolean;
  facetCount: string;
  hasZeroFacets: boolean;
  onTap: (type: SrpConditionName, isSelected: boolean) => void;
}

function ConditionOption({
  type,
  label,
  isChecked,
  facetCount,
  hasZeroFacets,
  onTap,
}: ConditionOptionProps): JSX.Element {
  return (
    <div className="condition-filter-checkbox-container">
      <LabeledCheckBox
        label={label}
        aria-label={`${type}-label`}
        isChecked={isChecked}
        onClick={(isSelected): void => onTap(type, isSelected)}
        style={{ width: 'auto', paddingRight: '5px' }}
        disabled={hasZeroFacets}
      />
      <span className={classNames({ 'zero-facets': hasZeroFacets })}>{`(${facetCount})`}</span>
      <style jsx>{`
        .condition-filter-checkbox-container {
          display: flex;
          align-items: center;
        }
        .zero-facets {
          color: ${colors.G2};
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

export default function ConditionFilter({
  expanded,
  conditionFacets,
}: ConditionFilterProps): JSX.Element {
  const logger = useEvents();
  const { sourcePage } = useContext(SrpEventContext);
  const { t } = useTranslation();
  const conditionFilter = useConditionFilter();
  const featuresFilter = useFeaturesTechnologyFilter();

  const facetCounts = useMemo(() => {
    function getFacetCount(type: keyof ConditionFacet): { count: string; hasZeroFacets: boolean } {
      const hasZeroFacets = !conditionFacets?.[type];
      return {
        count: hasZeroFacets ? '0' : formatNumberWithCommas(conditionFacets?.[type] as number),
        hasZeroFacets,
      };
    }

    return {
      new: getFacetCount('new'),
      used: getFacetCount('used'),
      cpo: getFacetCount('cpo'),
    };
  }, [conditionFacets]);

  function handleChangeCondition(name: SrpConditionName, isChecked: boolean): void {
    conditionFilter.setValue({
      ...conditionFilter.value,
      [name]: isChecked,
    });
  }

  function handleChangeWarranty(isChecked: boolean): void {
    featuresFilter.setValue({
      ...featuresFilter.value,
      warranty: isChecked,
    });
  }

  const conditions = [
    { type: 'new' as SrpConditionName, conditionType: Conditions.new },
    { type: 'used' as SrpConditionName, conditionType: Conditions.used },
    { type: 'certified pre-owned' as SrpConditionName, conditionType: Conditions.cpo },
  ];

  return (
    <Accordion
      title={t('srp.filters.condition.header')}
      expanded={expanded}
      onClick={(isExpanding: boolean): Promise<void> =>
        logger.log(ExpandCollapseTap({ feature: Features.condition, isExpanding, sourcePage }))
      }
    >
      <div className="condition-filter-inner-container">
        {conditions.map(({ type, conditionType }) => (
          <ConditionOption
            key={type}
            type={type}
            label={t(`srp.filters.condition.${type === 'certified pre-owned' ? 'cpo' : type}`)}
            isChecked={conditionFilter.value?.[type] ?? false}
            facetCount={facetCounts[type === 'certified pre-owned' ? 'cpo' : type].count}
            hasZeroFacets={facetCounts[type === 'certified pre-owned' ? 'cpo' : type].hasZeroFacets}
            onTap={(name: SrpConditionName, isChecked: boolean): void => {
              handleChangeCondition(name, isChecked);
              logger.log(ConditionTap(conditionType, sourcePage));
            }}
          />
        ))}
        <div className="condition-divider-container">
          <PageDivider />
        </div>
        <Toggle
          label={t('srp.filters.condition.inclWarranty')}
          id="incl-warranty"
          isToggled={featuresFilter.value?.warranty}
          onClick={(isChecked): void => {
            handleChangeWarranty(isChecked);
            logger.log(FeaturesTechnologyTap('warranty', sourcePage));
          }}
        />
      </div>
      <style jsx>{`
        .condition-filter-inner-container {
          padding-bottom: 8px;
        }
        .condition-divider-container {
          margin: 8px 0;
        }
      `}</style>
    </Accordion>
  );
}
